import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Form, Spinner } from 'react-bootstrap';
import { colors } from '../../brand-colors';

function Settings() {
  const [loading, setLoading] = useState(true);
  const [siteKey, setSiteKey] = useState('');
  const [siteUrl, setSiteUrl] = useState(null);
  const [siteConnectedAt, setSiteConnectedAt] = useState(null);
  const [siteLastPing, setSiteLastPing] = useState(null);
  const [showFullKey, setShowFullKey] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Secondary site
  const [siteKey2, setSiteKey2] = useState('');
  const [siteUrl2, setSiteUrl2] = useState(null);
  const [siteLabel2, setSiteLabel2] = useState('');
  const [siteConnectedAt2, setSiteConnectedAt2] = useState(null);
  const [showFullKey2, setShowFullKey2] = useState(false);
  const [copied2, setCopied2] = useState(false);

  // Elementor template settings
  const [elementorSettings, setElementorSettings] = useState({
    blog_template_id: '',
    page_template_id: ''
  });
  const [elementorTemplates, setElementorTemplates] = useState([]);
  const [elementorActive, setElementorActive] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [elementorSaving, setElementorSaving] = useState(false);
  const [elementorSaved, setElementorSaved] = useState(false);

  useEffect(() => {
    fetchSiteKey();
  }, []);

  const fetchSiteKey = async () => {
    try {
      const response = await fetch('/api/settings/site-key', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSiteKey(data.site_key);
        setSiteUrl(data.site_url);
        setSiteConnectedAt(data.site_connected_at);
        setSiteLastPing(data.site_last_ping);
        setSiteKey2(data.site_key_2 || '');
        setSiteUrl2(data.site_url_2 || null);
        setSiteLabel2(data.site_label_2 || '');
        setSiteConnectedAt2(data.site_connected_at_2 || null);

        // If site is connected, fetch Elementor settings
        if (data.site_url) {
          fetchElementorSettings();
        }
      }
    } catch (err) {
      setError('Failed to load site key');
    } finally {
      setLoading(false);
    }
  };

  const fetchElementorSettings = async () => {
    try {
      const res = await fetch('/api/settings/elementor', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setElementorSettings({
          blog_template_id: data.blog_template_id || '',
          page_template_id: data.page_template_id || ''
        });
      }
    } catch (err) {
      console.error('Error fetching Elementor settings:', err);
    }
  };

  const fetchElementorTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await fetch('/api/settings/elementor/templates', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setElementorActive(data.elementor_active);
        setElementorTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Error fetching Elementor templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveElementorSettings = async () => {
    setElementorSaving(true);
    setElementorSaved(false);
    try {
      const res = await fetch('/api/settings/elementor', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blog_template_id: elementorSettings.blog_template_id || null,
          page_template_id: elementorSettings.page_template_id || null
        })
      });
      if (res.ok) {
        setElementorSaved(true);
        setTimeout(() => setElementorSaved(false), 3000);
      }
    } catch (err) {
      console.error('Error saving Elementor settings:', err);
    } finally {
      setElementorSaving(false);
    }
  };

  const regenerateKey = async () => {
    if (!window.confirm('Regenerating your site key will disconnect your current WordPress site. Continue?')) {
      return;
    }

    setRegenerating(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/settings/site-key/regenerate', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSiteKey(data.site_key);
        setSiteUrl(null);
        setSiteConnectedAt(null);
        setSiteLastPing(null);
        setSuccess('Site key regenerated. Update your WordPress plugin with the new key.');
      } else {
        setError('Failed to regenerate site key');
      }
    } catch (err) {
      setError('Failed to regenerate site key');
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(siteKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = siteKey;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Never';
    return new Date(isoString).toLocaleString();
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading settings...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h1 className="mb-4">Settings</h1>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Website Integration */}
      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: colors.greyLightest, borderBottom: `2px solid ${colors.goldMid}` }}>
          <strong>Website Integration</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col lg={8}>
              <h5>WordPress Plugin</h5>
              <p className="text-muted">
                Connect your WordPress site to AI Profit Engineer to publish content directly,
                track analytics, and inject SEO schema markup.
              </p>

              {/* Connection Status */}
              <div className="mb-4">
                <strong>Status: </strong>
                {siteUrl ? (
                  <Badge bg="success">Connected</Badge>
                ) : (
                  <Badge bg="warning" text="dark">Not Connected</Badge>
                )}
                {siteUrl && (
                  <span className="ms-2 text-muted">
                    to <a href={siteUrl} target="_blank" rel="noopener noreferrer">{siteUrl}</a>
                  </span>
                )}
              </div>

              {siteUrl && (
                <Row className="mb-4">
                  <Col sm={6}>
                    <small className="text-muted">Connected:</small>
                    <div>{formatDate(siteConnectedAt)}</div>
                  </Col>
                  <Col sm={6}>
                    <small className="text-muted">Last Activity:</small>
                    <div>{formatDate(siteLastPing)}</div>
                  </Col>
                </Row>
              )}

              {/* Site Key */}
              <Form.Group className="mb-3">
                <Form.Label><strong>Your Site Key</strong></Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type={showFullKey ? 'text' : 'password'}
                    value={siteKey}
                    readOnly
                    style={{ fontFamily: 'monospace', maxWidth: '400px' }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowFullKey(!showFullKey)}
                    title={showFullKey ? 'Hide' : 'Show'}
                  >
                    {showFullKey ? '🙈' : '👁️'}
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={copyToClipboard}
                  >
                    {copied ? '✓ Copied!' : 'Copy'}
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  Enter this key in your WordPress plugin settings to connect your site.
                </Form.Text>
              </Form.Group>

              {/* Actions */}
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="warning"
                  onClick={() => {
                    fetch('/api/settings/plugin/download', { credentials: 'include' })
                      .then(res => res.blob())
                      .then(blob => {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'profitengine-connector.zip';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                      });
                  }}
                >
                  Download Plugin
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={regenerateKey}
                  disabled={regenerating}
                >
                  {regenerating ? 'Regenerating...' : 'Regenerate Key'}
                </Button>
              </div>
            </Col>

            <Col lg={4}>
              <Card className="bg-light">
                <Card.Body>
                  <h6>Setup Instructions</h6>
                  <ol className="mb-0" style={{ paddingLeft: '1.2rem' }}>
                    <li>Download the plugin using the button</li>
                    <li>In WordPress, go to Plugins &gt; Add New &gt; Upload</li>
                    <li>Upload the zip file and activate</li>
                    <li>Go to Settings &gt; ProfitEngine Connector</li>
                    <li>Enter your Site Key and click Validate</li>
                  </ol>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Secondary WordPress Site */}
      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: colors.greyLightest, borderBottom: `2px solid ${colors.goldMid}` }}>
          <strong>Secondary WordPress Site</strong>
          <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
            Connect a second domain (e.g., blog on one domain, funnels on another)
          </span>
        </Card.Header>
        <Card.Body>
          {!siteKey2 ? (
            <div>
              <p className="text-muted">
                If your business uses two WordPress sites (e.g., blog content on one domain and sales pages on another),
                generate a second key and install the plugin on that site too.
              </p>
              <Button
                variant="outline-dark"
                onClick={async () => {
                  try {
                    const res = await fetch('/api/settings/site-key-2/generate', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ label: 'Secondary Site' }),
                    });
                    if (res.ok) {
                      const data = await res.json();
                      setSiteKey2(data.site_key_2);
                      setSiteLabel2(data.site_label_2);
                      setSuccess('Secondary site key generated. Install the plugin on your second WordPress site and use this key.');
                    }
                  } catch (err) {
                    setError('Failed to generate secondary key');
                  }
                }}
              >
                Generate Second Site Key
              </Button>
            </div>
          ) : (
            <div>
              <div className="mb-3">
                <strong>Status: </strong>
                {siteUrl2 ? (
                  <Badge bg="success">Connected</Badge>
                ) : (
                  <Badge bg="warning" text="dark">Not Connected</Badge>
                )}
                {siteUrl2 && (
                  <span className="ms-2 text-muted">
                    to <a href={siteUrl2} target="_blank" rel="noopener noreferrer">{siteUrl2}</a>
                  </span>
                )}
                {siteConnectedAt2 && (
                  <span className="ms-2 text-muted" style={{ fontSize: '0.85rem' }}>
                    (connected {new Date(siteConnectedAt2).toLocaleDateString()})
                  </span>
                )}
              </div>

              <Form.Group className="mb-3">
                <Form.Label><strong>Secondary Site Key</strong></Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type={showFullKey2 ? 'text' : 'password'}
                    value={siteKey2}
                    readOnly
                    style={{ fontFamily: 'monospace', maxWidth: '400px' }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowFullKey2(!showFullKey2)}
                    title={showFullKey2 ? 'Hide' : 'Show'}
                  >
                    {showFullKey2 ? '🙈' : '👁️'}
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      navigator.clipboard.writeText(siteKey2);
                      setCopied2(true);
                      setTimeout(() => setCopied2(false), 2000);
                    }}
                  >
                    {copied2 ? '✓ Copied!' : 'Copy'}
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  Enter this key in the WordPress plugin on your second site.
                </Form.Text>
              </Form.Group>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Elementor Template Settings - Only show when WordPress is connected */}
      {siteUrl && (
        <Card className="shadow-sm mb-4">
          <Card.Header style={{ backgroundColor: colors.greyLightest, borderBottom: `2px solid ${colors.goldMid}` }}>
            <strong>Elementor Template Settings</strong>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">
              Configure default Elementor templates for content published from AI Profit Engineer to your WordPress site.
            </p>

            {!elementorTemplates.length && !loadingTemplates ? (
              <div>
                <Button
                  variant="outline-primary"
                  onClick={fetchElementorTemplates}
                  disabled={loadingTemplates}
                >
                  Load Elementor Templates
                </Button>
                <Form.Text className="text-muted d-block mt-2">
                  Click to fetch available Elementor templates from your WordPress site.
                </Form.Text>
              </div>
            ) : loadingTemplates ? (
              <div className="d-flex align-items-center text-muted">
                <Spinner size="sm" className="me-2" />
                Loading templates from your WordPress site...
              </div>
            ) : !elementorActive ? (
              <Alert variant="warning" className="mb-0">
                <strong>Elementor Not Active</strong>
                <p className="mb-0 mt-1">
                  Elementor is not active on your WordPress site. Install and activate Elementor to use template features.
                </p>
              </Alert>
            ) : (
              <div>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Blog Post Template</strong></Form.Label>
                      <Form.Select
                        value={elementorSettings.blog_template_id}
                        onChange={(e) => setElementorSettings({
                          ...elementorSettings,
                          blog_template_id: e.target.value
                        })}
                      >
                        <option value="">Use WordPress default</option>
                        {elementorTemplates.map(tpl => (
                          <option key={tpl.id} value={tpl.id}>
                            {tpl.title} (ID: {tpl.id})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Applied when publishing blog posts
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Page Template</strong></Form.Label>
                      <Form.Select
                        value={elementorSettings.page_template_id}
                        onChange={(e) => setElementorSettings({
                          ...elementorSettings,
                          page_template_id: e.target.value
                        })}
                      >
                        <option value="">Use WordPress default</option>
                        {elementorTemplates.map(tpl => (
                          <option key={tpl.id} value={tpl.id}>
                            {tpl.title} (ID: {tpl.id})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Applied when publishing pages (sales pages, landing pages, etc.)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="dark"
                    onClick={saveElementorSettings}
                    disabled={elementorSaving}
                    style={{ backgroundColor: colors.greyDarkest }}
                  >
                    {elementorSaving ? 'Saving...' : 'Save Template Settings'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={fetchElementorTemplates}
                    disabled={loadingTemplates}
                  >
                    Refresh Templates
                  </Button>
                  {elementorSaved && (
                    <span className="text-success">
                      ✓ Settings saved!
                    </span>
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Plugin Features */}
      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: colors.greyLightest }}>
          <strong>Plugin Features</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <div className="d-flex align-items-start">
                <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>📄</span>
                <div>
                  <strong>Content Publishing</strong>
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    Publish sales pages, funnels, and lead magnet pages directly from AI Profit Engineer to your WordPress site.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex align-items-start">
                <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>📊</span>
                <div>
                  <strong>Analytics Tracking</strong>
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    Automatic visitor tracking and conversion measurement via PostHog.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex align-items-start">
                <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>🔍</span>
                <div>
                  <strong>SEO Schema Markup</strong>
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    Inject LocalBusiness and Authority/Person schema for better search visibility.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <div className="d-flex align-items-start">
                <span style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>🧲</span>
                <div>
                  <strong>Lead Capture Widgets</strong>
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    Embed lead magnet widgets anywhere using simple shortcodes.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Settings;
